import { COMMUNITY_KIND } from '@/types';

const COMMUNITY_KIND_VALUES = new Set<string>(Object.values(COMMUNITY_KIND));

export const DEFAULT_COMMUNITY_KIND = COMMUNITY_KIND.BUSINESS;

type CommunityKindParams = {
  kind: string | undefined;
};

export const isCommunityKind = (params: CommunityKindParams): params is { kind: COMMUNITY_KIND } => {
  return Boolean(params.kind) && COMMUNITY_KIND_VALUES.has(params.kind as string);
};

export const resolveCommunityKind = (params: CommunityKindParams): COMMUNITY_KIND => {
  if (params.kind === 'camino') {
    return COMMUNITY_KIND.MEMBER;
  }

  if (isCommunityKind(params)) {
    return params.kind;
  }

  return DEFAULT_COMMUNITY_KIND;
};

const COMMUNITY_LIST_CREATE_MODAL_TITLE_KEY_BY_KIND: Record<COMMUNITY_KIND, string> = {
  [COMMUNITY_KIND.FAMILY]: 'community.lists.createModalTitleFamily',
  [COMMUNITY_KIND.BUSINESS]: 'community.lists.createModalTitleBusiness',
  [COMMUNITY_KIND.MEMBER]: 'community.lists.createModalTitleMember',
};

export const getCommunityListCreateModalTitleKey = ({ kind }: { kind: COMMUNITY_KIND }): string => {
  return COMMUNITY_LIST_CREATE_MODAL_TITLE_KEY_BY_KIND[kind];
};

const COMMUNITY_CARD_COUNT_LABEL_KEY_BY_KIND: Record<COMMUNITY_KIND, string> = {
  [COMMUNITY_KIND.FAMILY]: 'community.cards.pageTitleFamilyCount',
  [COMMUNITY_KIND.BUSINESS]: 'community.cards.pageTitleBusinessCount',
  [COMMUNITY_KIND.MEMBER]: 'community.cards.pageTitleMemberCount',
};

export const getCommunityCardCountLabelKey = ({ kind }: { kind: COMMUNITY_KIND }): string => {
  return COMMUNITY_CARD_COUNT_LABEL_KEY_BY_KIND[kind];
};

const COMMUNITY_CARD_ADD_NEW_LABEL_KEY_BY_KIND: Record<COMMUNITY_KIND, string> = {
  [COMMUNITY_KIND.FAMILY]: 'community.cards.addNewFamily',
  [COMMUNITY_KIND.BUSINESS]: 'community.cards.addNewBusiness',
  [COMMUNITY_KIND.MEMBER]: 'community.cards.addNewMember',
};

export const getCommunityCardAddNewLabelKey = ({ kind }: { kind: COMMUNITY_KIND }): string => {
  return COMMUNITY_CARD_ADD_NEW_LABEL_KEY_BY_KIND[kind];
};

const COMMUNITY_CARD_CREATE_BUTTON_LABEL_KEY_BY_KIND: Record<COMMUNITY_KIND, string> = {
  [COMMUNITY_KIND.FAMILY]: 'community.cards.createFamilyButtonLabel',
  [COMMUNITY_KIND.BUSINESS]: 'community.cards.createBusinessButtonLabel',
  [COMMUNITY_KIND.MEMBER]: 'community.cards.createMemberButtonLabel',
};

export const getCommunityCardCreateButtonLabelKey = ({ kind }: { kind: COMMUNITY_KIND }): string => {
  return COMMUNITY_CARD_CREATE_BUTTON_LABEL_KEY_BY_KIND[kind];
};

const COMMUNITY_CARD_DETAIL_TITLE_KEY_BY_KIND: Record<COMMUNITY_KIND, string> = {
  [COMMUNITY_KIND.FAMILY]: 'community.cards.detailTitleFamily',
  [COMMUNITY_KIND.BUSINESS]: 'community.cards.detailTitleBusiness',
  [COMMUNITY_KIND.MEMBER]: 'community.cards.detailTitleMember',
};

export const getCommunityCardDetailTitleKey = ({ kind }: { kind: COMMUNITY_KIND }): string => {
  return COMMUNITY_CARD_DETAIL_TITLE_KEY_BY_KIND[kind];
};

const COMMUNITY_CARD_EDIT_TITLE_KEY_BY_KIND: Record<COMMUNITY_KIND, string> = {
  [COMMUNITY_KIND.FAMILY]: 'community.cards.editFamily',
  [COMMUNITY_KIND.BUSINESS]: 'community.cards.editBusiness',
  [COMMUNITY_KIND.MEMBER]: 'community.cards.editMember',
};

export const getCommunityCardEditTitleKey = ({ kind }: { kind: COMMUNITY_KIND }): string => {
  return COMMUNITY_CARD_EDIT_TITLE_KEY_BY_KIND[kind];
};

type CommunityCardInfoLabels = {
  infoTabKey: string;
  memberTabKey: string;
  nameLabelKey: string;
  listNameLabelKey: string;
  namePlaceholderKey: string;
  emailLabelKey: string;
  emailPlaceholderKey: string;
  phoneLabelKey: string;
  listsTitleKey: string;
};

const COMMUNITY_CARD_INFO_LABELS_BY_KIND: Record<COMMUNITY_KIND, CommunityCardInfoLabels> = {
  [COMMUNITY_KIND.BUSINESS]: {
    infoTabKey: 'community.cards.businessInfoTab',
    memberTabKey: 'community.cards.businessMemberTab',
    nameLabelKey: 'community.cards.businessNameLabel',
    listNameLabelKey: 'community.cards.businessNameLabel',
    namePlaceholderKey: 'community.cards.businessNamePlaceholder',
    emailLabelKey: 'community.cards.businessEmailLabel',
    emailPlaceholderKey: 'community.cards.businessEmailPlaceholder',
    phoneLabelKey: 'community.cards.businessPhoneLabel',
    listsTitleKey: 'community.cards.businessListTitle',
  },
  [COMMUNITY_KIND.FAMILY]: {
    infoTabKey: 'community.cards.familyInfoTab',
    memberTabKey: 'community.cards.familyMemberTab',
    nameLabelKey: 'community.cards.familyNameLabel',
    listNameLabelKey: 'community.cards.family',
    namePlaceholderKey: 'community.cards.familyNamePlaceholder',
    emailLabelKey: 'community.cards.familyEmailLabel',
    emailPlaceholderKey: 'community.cards.familyEmailPlaceholder',
    phoneLabelKey: 'community.cards.familyPhoneLabel',
    listsTitleKey: 'community.cards.familyListTitle',
  },
  [COMMUNITY_KIND.MEMBER]: {
    infoTabKey: 'community.cards.memberInfoTab',
    memberTabKey: 'community.cards.memberPeopleTab',
    nameLabelKey: 'community.cards.memberNameLabel',
    listNameLabelKey: 'community.cards.memberNameLabel',
    namePlaceholderKey: 'community.cards.memberNamePlaceholder',
    emailLabelKey: 'community.cards.memberEmailLabel',
    emailPlaceholderKey: 'community.cards.memberEmailPlaceholder',
    phoneLabelKey: 'community.cards.memberPhoneLabel',
    listsTitleKey: 'community.cards.memberListTitle',
  },
};

export const getCommunityCardInfoLabels = ({ kind }: { kind: COMMUNITY_KIND }): CommunityCardInfoLabels => {
  return COMMUNITY_CARD_INFO_LABELS_BY_KIND[kind];
};
